import { type ReactNode, useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import AdminLayout from '@/components/AdminLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Check, Pencil, Plus, Star, Trash2 } from 'lucide-react'
import type { AdminEventService } from '@/types'

type Props = {
  pending: AdminEventService[]
  services: AdminEventService[]
  filters: { search: string }
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function AdminEventServicesIndex({ pending, services, filters }: Props) {
  const [search, setSearch] = useState(filters.search)
  const [deleteId, setDeleteId] = useState<string | null>(null)

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    router.get('/admin/event_services', { search }, { preserveState: true })
  }

  function approve(id: string) {
    router.post(`/admin/event_services/${id}/approve`, {}, { preserveScroll: true })
  }

  function confirmDelete() {
    if (!deleteId) return
    router.delete(`/admin/event_services/${deleteId}`)
    setDeleteId(null)
  }

  return (
    <>
      <Head title="Admin - Event Services" />
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-semibold tracking-tight font-serif">Event Services</h1>
          <Link href="/admin/event_services/new">
            <Button>
              <Plus className="size-4 mr-1" />
              Add Service
            </Button>
          </Link>
        </div>

        {pending.length > 0 && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="flex items-center gap-2">
                Pending submissions
                <Badge variant="default">{pending.length}</Badge>
              </CardTitle>
              <p className="text-sm text-muted-foreground">Public submissions awaiting review</p>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Service Type</TableHead>
                    <TableHead>Submitted by</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead className="w-[180px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pending.map(service => (
                    <TableRow key={service.id}>
                      <TableCell className="font-medium">{service.name}</TableCell>
                      <TableCell className="text-muted-foreground">{service.service_type}</TableCell>
                      <TableCell className="text-muted-foreground">{service.contact_email || '—'}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {[service.city, service.state].filter(Boolean).join(', ') || '—'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{formatDate(service.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            onClick={() => approve(service.id)}
                          >
                            <Check className="size-3.5 mr-1" />
                            Approve
                          </Button>
                          <Link href={`/admin/event_services/${service.id}/edit`}>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <Pencil className="size-3.5" />
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => setDeleteId(service.id)}
                          >
                            <Trash2 className="size-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        <div>
          <form onSubmit={handleSearch} className="max-w-sm">
            <Input
              placeholder="Search by name or city..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </form>

          <Table className="mt-6">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Service Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Price Range</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-[100px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {services.map(service => (
                <TableRow key={service.id}>
                  <TableCell className="font-medium">
                    <span className="inline-flex items-center gap-1.5">
                      {service.name}
                      {service.featured && <Star className="size-3.5 fill-primary text-primary" />}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{service.service_type}</TableCell>
                  <TableCell>
                    <Badge variant={service.status === 'verified' ? 'default' : service.status === 'published' ? 'secondary' : 'outline'} className="capitalize">
                      {service.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {[service.city, service.state].filter(Boolean).join(', ') || '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{service.price_range || '—'}</TableCell>
                  <TableCell className="text-muted-foreground">{formatDate(service.created_at)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Link href={`/admin/event_services/${service.id}/edit`}>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Pencil className="size-3.5" />
                        </Button>
                      </Link>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => setDeleteId(service.id)}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {services.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    No event services found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Event Service</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this event service? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

AdminEventServicesIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
